import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
} from '@chakra-ui/react';
import axios from 'axios';

const AdminEarnings = () => {
  const [earningsData, setEarningsData] = useState(null);
  const [shareConfig, setShareConfig] = useState({
    adminShare: 70,
    creatorShare: 30,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchEarningsData();
  }, []);

  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) throw new Error('Unauthorized');
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_BASE_URL ||
        window.location.origin ||
        'http://localhost:3030'
      )
        .trim()
        .replace(/\/$/, '');

      const [earningsRes, shareRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/admin/earnings`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(`${apiBaseUrl}/api/admin/revenue-share`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (earningsRes.data.success) {
        setEarningsData(earningsRes.data.data);
      } else {
        setError('Failed to fetch earnings data');
      }

      if (shareRes.data.success) {
        setShareConfig({
          adminShare: shareRes.data.data?.adminShare ?? 70,
          creatorShare: shareRes.data.data?.creatorShare ?? 30,
        });
      }
    } catch (err) {
      console.error('Error fetching earnings:', err);
      setError(err.response?.data?.message || 'Failed to fetch earnings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="400px"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box p={6}>
      <Heading mb={6} size="lg">
        Admin Earnings Dashboard
      </Heading>

      <Grid
        templateColumns={{
          base: '1fr',
          md: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        }}
        gap={6}
      >
        {/* Total Admin Earnings */}
        <GridItem>
          <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
            <CardHeader pb={2}>
              <Heading size="md" color="green.500">
                💰 Total Admin Earnings
              </Heading>
            </CardHeader>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  From Chapter Audio Purchases
                </StatLabel>
                <StatNumber fontSize="3xl" color="green.500">
                  {earningsData?.totalAdminEarnings || 0} 💎
                </StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {shareConfig.adminShare}% of each purchase
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        {/* Total Transactions */}
        <GridItem>
          <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
            <CardHeader pb={2}>
              <Heading size="md" color="blue.500">
                📊 Total Transactions
              </Heading>
            </CardHeader>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  Audio Chapter Purchases
                </StatLabel>
                <StatNumber fontSize="3xl" color="blue.500">
                  {earningsData?.totalTransactions || 0}
                </StatNumber>
                <StatHelpText>Successful purchases</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>

        {/* Average Earnings per Transaction */}
        <GridItem>
          <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
            <CardHeader pb={2}>
              <Heading size="md" color="purple.500">
                📈 Average Earnings
              </Heading>
            </CardHeader>
            <CardBody>
              <Stat>
                <StatLabel fontSize="sm" color="gray.600">
                  Per Transaction
                </StatLabel>
                <StatNumber fontSize="3xl" color="purple.500">
                  {earningsData?.totalTransactions > 0
                    ? (
                        earningsData.totalAdminEarnings /
                        earningsData.totalTransactions
                      ).toFixed(1)
                    : 0}{' '}
                  💎
                </StatNumber>
                <StatHelpText>Average admin share</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Revenue Breakdown Info */}
      <Box mt={8}>
        <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
          <CardHeader>
            <Heading size="md">Revenue Sharing Model</Heading>
          </CardHeader>
          <CardBody>
            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
              <Box flex={1}>
                <Text fontWeight="bold" color="green.500" mb={2}>
                  Creator Share: {shareConfig.creatorShare}%
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Story owners earn this share of each chapter audio purchase
                  from their content.
                </Text>
              </Box>
              <Box flex={1}>
                <Text fontWeight="bold" color="blue.500" mb={2}>
                  Admin Share: {shareConfig.adminShare}%
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Platform commission retained per transaction to cover
                  operational costs.
                </Text>
              </Box>
            </Flex>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};

export default AdminEarnings;
